import { useState } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import CloseIcon from '@mui/icons-material/CloseRounded';
import CheckIcon from '@mui/icons-material/CheckRounded';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesomeRounded';
import LockIcon from '@mui/icons-material/LockOutlined';
import BoltIcon from '@mui/icons-material/BoltRounded';

import api from '../../services/api';

const shimmerKeyframes = `
@keyframes pricing-shimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
}
@keyframes pricing-fade-in {
    from { opacity: 0; transform: translateY(14px); }
    to   { opacity: 1; transform: translateY(0); }
}
`;

const PLANS = [
    {
        key: 'pro_light',
        name: 'PRO Light',
        price: 19,
        description: 'Ideale per professionisti e creator che vogliono accelerare il marketing.',
        recommended: false,
        icon: <BoltIcon sx={{ fontSize: '1rem' }} />,
        features: [
            {
                label: '2.000.000 caratteri al mese',
                detail: 'Agente AI · Chat · Post LinkedIn · Articoli SEO · Campagne',
            },
            {
                label: '50 immagini al mese',
                detail: 'Generazione e modifica immagini con AI',
            },
            {
                label: 'Video AI (Google VEO)',
                detail: 'Pay-Per-Use',
            },
            {
                label: 'Calendari editoriali',
                detail: 'Incluso',
            },
        ],
    },
    {
        key: 'pro_max',
        name: 'PRO Max',
        price: 59,
        description: 'Per team e agenzie che lavorano su volumi elevati e non vogliono limiti.',
        recommended: true,
        icon: <AutoAwesomeIcon sx={{ fontSize: '1rem' }} />,
        features: [
            {
                label: '8.000.000 caratteri al mese',
                detail: 'Agente AI · Chat · Post LinkedIn · Articoli SEO · Campagne',
            },
            {
                label: '200 immagini al mese',
                detail: 'Generazione e modifica immagini con AI',
            },
            {
                label: 'Video AI — 30 sec/mese (Google VEO)',
                detail: 'Inclusi · poi Pay-Per-Use',
            },
            {
                label: 'Calendari editoriali',
                detail: 'Incluso',
            },
        ],
    },
];

const PlanCard = ({ plan, selected, onSelect, currentPlan, onSubscribe, loading, loadingPlan }) => {
    const isCurrentPlan = currentPlan === plan.key;
    const isSelected = selected === plan.key;
    const isLoading = loadingPlan === plan.key && loading;
    const [hovered, setHovered] = useState(false);

    return (
        <Box
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => !isCurrentPlan && onSelect(plan.key)}
            sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                cursor: isCurrentPlan ? 'default' : 'pointer',
            }}
        >
            {/* Recommended floating badge */}
            {plan.recommended && (
                <Box sx={{
                    position: 'absolute',
                    top: -15,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 3,
                    background: isSelected ? '#701280' : '#18181B',
                    color: '#fff',
                    px: 1.8,
                    py: 0.45,
                    borderRadius: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    whiteSpace: 'nowrap',
                    transition: 'background 0.25s ease',
                    boxShadow: isSelected ? '0 3px 14px rgba(112,18,128,0.4)' : '0 2px 10px rgba(0,0,0,0.2)',
                }}>
                    <AutoAwesomeIcon sx={{ fontSize: '0.65rem' }} />
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Consigliato
                    </Typography>
                </Box>
            )}

            {/* Card body */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                background: isSelected ? 'rgba(112,18,128,0.03)' : '#fff',
                border: isSelected
                    ? '2px solid #701280'
                    : hovered
                        ? '2px solid rgba(112,18,128,0.4)'
                        : plan.recommended
                            ? '2px solid #18181B'
                            : '2px solid #E8E8E8',
                borderRadius: '18px',
                p: 2.5,
                transition: 'all 0.22s ease',
                boxShadow: isSelected
                    ? '0 0 0 5px rgba(112,18,128,0.08), 0 8px 32px rgba(112,18,128,0.12)'
                    : hovered
                        ? '0 6px 28px rgba(0,0,0,0.1)'
                        : plan.recommended
                            ? '0 4px 20px rgba(0,0,0,0.09)'
                            : '0 2px 8px rgba(0,0,0,0.05)',
                transform: hovered && !isSelected ? 'translateY(-2px)' : 'none',
            }}>
                {/* Selection checkmark */}
                {isSelected && (
                    <Box sx={{
                        position: 'absolute',
                        top: plan.recommended ? 6 : -10,
                        right: 14,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#701280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 3px rgba(112,18,128,0.15)',
                        zIndex: 2,
                    }}>
                        <CheckIcon sx={{ fontSize: '0.8rem', color: '#fff' }} />
                    </Box>
                )}

                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                    <Box sx={{
                        width: 28, height: 28, borderRadius: '8px',
                        background: isSelected ? 'rgba(112,18,128,0.12)' : 'rgba(0,0,0,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: isSelected ? '#701280' : '#6B6B7B',
                        transition: 'all 0.2s',
                    }}>
                        {plan.icon}
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#18181B', letterSpacing: '-0.2px' }}>
                        {plan.name}
                    </Typography>
                </Box>

                <Typography sx={{ fontSize: '0.76rem', color: '#7B7B8B', lineHeight: 1.5, mb: 1.8, minHeight: 36 }}>
                    {plan.description}
                </Typography>

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.4, mb: 1.8 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#18181B', mb: '5px' }}>€</Typography>
                    <Typography sx={{ fontSize: '2.6rem', fontWeight: 900, color: '#18181B', lineHeight: 1, letterSpacing: '-2px' }}>
                        {plan.price}
                    </Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#9B9BA8', mb: '5px', ml: 0.3 }}>/mese</Typography>
                </Box>

                {/* CTA */}
                <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    disabled={isCurrentPlan || isLoading}
                    onClick={(e) => { e.stopPropagation(); !isCurrentPlan && onSubscribe(plan.key); }}
                    sx={{
                        background: plan.recommended
                            ? 'linear-gradient(135deg, #701280, #9C3AAE)'
                            : '#18181B',
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        py: 1.1,
                        mb: 2,
                        boxShadow: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                            background: plan.recommended ? 'linear-gradient(135deg, #5A0F68, #7A2890)' : '#27272A',
                            boxShadow: plan.recommended
                                ? '0 4px 16px rgba(112,18,128,0.35)'
                                : '0 4px 16px rgba(0,0,0,0.2)',
                            transform: 'translateY(-1px)',
                        },
                        '&:disabled': { background: 'rgba(0,0,0,0.09)', color: 'rgba(0,0,0,0.3)', boxShadow: 'none', transform: 'none' },
                    }}
                >
                    {isLoading
                        ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                        : isCurrentPlan ? 'Piano attuale' : `Attiva ${plan.name}`}
                </Button>

                {/* Divider */}
                <Box sx={{ borderTop: '1px solid', borderColor: isSelected ? 'rgba(112,18,128,0.15)' : 'rgba(0,0,0,0.07)', mb: 1.5 }} />

                {/* Features label */}
                <Typography sx={{
                    fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: isSelected ? '#701280' : '#ABABBB',
                    mb: 1.2,
                }}>
                    Cosa include
                </Typography>

                {/* Feature list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {plan.features.map((f, i) => {
                        const isSubtitle = f.detail !== 'Incluso' && f.detail !== 'Illimitato';
                        const isMuted = !!f.muted;
                        return (
                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', opacity: isMuted ? 0.6 : 1 }}>
                                <CheckIcon sx={{ fontSize: '0.8rem', color: isMuted ? '#CDCDD8' : (isSelected ? '#701280' : '#ABABBB'), flexShrink: 0, mt: '2px' }} />
                                <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: isMuted ? '#888' : '#1C1C28', lineHeight: 1.3 }}>
                                        {f.label}
                                    </Typography>
                                    {isSubtitle ? (
                                        <Typography sx={{ fontSize: '0.7rem', color: '#9B9BA8', mt: 0.2, lineHeight: 1.4 }}>
                                            {f.detail}
                                        </Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: isSelected ? '#701280' : '#9B9BA8', mt: 0.1 }}>
                                            {f.detail}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default function PricingModal({ open, onClose, currentPlan = null, onSuccess }) {
    const [selected, setSelected] = useState('pro_max');
    const [loading, setLoading] = useState(false);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [error, setError] = useState('');

    const handleSubscribe = async (planKey) => {
        setLoading(true);
        setLoadingPlan(planKey);
        setError('');
        try {
            const res = await api.post('/billing/stripe/checkout/', { plan: planKey });
            if (res.data?.dev_mode && res.data?.success) {
                onClose();
                if (onSuccess) onSuccess(res.data.plan_label);
            } else if (res.data?.upgraded) {
                onClose();
                if (onSuccess) onSuccess(res.data.plan_label);
            } else if (res.data?.url) {
                window.location.href = res.data.url;
            } else {
                setError(res.data?.error || 'Errore durante il checkout');
            }
        } catch (e) {
            setError(e?.response?.data?.error || 'Errore di connessione');
        } finally {
            setLoading(false);
            setLoadingPlan(null);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    background: '#F8F7FA',
                    boxShadow: '0 32px 100px rgba(0,0,0,0.16)',
                    overflow: 'hidden',
                    maxHeight: '92vh',
                }
            }}
        >
            <style>{shimmerKeyframes}</style>
            <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                {/* Close button */}
                <Box sx={{ position: 'absolute', top: 14, right: 14, zIndex: 10 }}>
                    <IconButton
                        onClick={onClose}
                        size="small"
                        sx={{ color: '#9B9BA8', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)', '&:hover': { color: '#18181B', background: '#fff' } }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                {/* Header */}
                <Box sx={{ textAlign: 'center', pt: 2.5, pb: 2, px: 3 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.4rem', sm: '1.7rem' }, color: '#18181B', lineHeight: 1.15, letterSpacing: '-0.5px', mb: 0.8 }}>
                        Passa ad{' '}
                        <Box component="span" sx={{
                            background: 'linear-gradient(135deg, #701280 0%, #C850C0 50%, #9C3AAE 100%)',
                            backgroundSize: '200% auto',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            animation: 'pricing-shimmer 3s linear infinite',
                        }}>
                            AdMind Pro
                        </Box>
                    </Typography>

                    <Box sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        mt: 1.2,
                        background: 'linear-gradient(135deg, rgba(112,18,128,0.12) 0%, rgba(200,80,192,0.12) 100%)',
                        border: '1px solid rgba(112,18,128,0.25)',
                        borderRadius: '20px',
                        px: 2,
                        py: 0.6,
                    }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#701280' }}>
                            annulli quando vuoi
                        </Typography>
                    </Box>
                </Box>

                {/* Cards */}
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    pt: 3,
                    pb: 1,
                    display: 'flex',
                    gap: 2,
                    flex: 1,
                    alignItems: 'stretch',
                }}>
                    {PLANS.map(plan => (
                        <PlanCard
                            key={plan.key}
                            plan={plan}
                            selected={selected}
                            onSelect={setSelected}
                            currentPlan={currentPlan}
                            onSubscribe={handleSubscribe}
                            loading={loading}
                            loadingPlan={loadingPlan}
                        />
                    ))}
                </Box>

                {/* Footer */}
                <Box sx={{ px: 3, py: 2, textAlign: 'center' }}>
                    {error && (
                        <Typography sx={{ color: '#EF4444', fontSize: '0.8rem', mb: 1 }}>{error}</Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8 }}>
                        <LockIcon sx={{ fontSize: '0.75rem', color: '#BABAC8' }} />
                        <Typography sx={{ fontSize: '0.71rem', color: '#BABAC8' }}>
                            Pagamento sicuro via Stripe · Rinnovo automatico mensile · Annulla quando vuoi
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}
